import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import BSPagination from "../../Components/pagination";
import {
  changeTagStatus,
  deleteTagById,
  getPaginatedGameTags,
  searchTags,
} from "../../services/axios/apiManager";
import { handleErrors } from "../../services/dataManager";
import { useNavigate } from "react-router-dom";
import AddEditTagModal from "./addEditTag";
import { toast } from "sonner";

import $ from "jquery";
import "datatables.net-bs5";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";

const TagList = ({ tags, setTags, pagination, setPagination, granted }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [modalOptions, setModalOptions] = useState({});
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState({});
  const [showModal, setShowModal] = useState(false);
  const tableRef = useRef(null);

  // Fetch paginated list
  const fetchTags = async (pgntn, str = "") => {
    const res = await getPaginatedGameTags(
      pgntn.currentPage,
      pgntn.pageSize,
      str
    );
    if (res?.data?.success) {
      setTags(res.data.data);
      res.data.pagination && setPagination(res.data.pagination);
    } else {
      handleErrors(res, navigate, "Tags List:");
    }
  };

  // call fetch whenever page or size changes
  useEffect(() => {
    fetchTags(pagination, query ? query : "");
  }, [pagination.currentPage, pagination.pageSize]);

  // Search
  const handleSearch = async (e) => {
    const string = e.target.value;
    setQuery(string);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    if (string.length > 0) {
      const res = await searchTags(string);
      if (res?.data?.success) {
        setTags(res.data.data);
        res.data.pagination && setPagination(res.data.pagination);
      } else {
        handleErrors(res, navigate, "Search Tags:");
      }
    } else {
      fetchTags(pagination);
    }
  };

  // Pagination
  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  // Modals
  const handleStatusChange = (tag) => {
    if (!granted.some((perm) => perm.type === "toggle-status")) {
      toast.error("You do not have neccessary permission to change stattus.", { duration: 3000 });
      return;
    }
    setModalOptions({
      header: "Change Status",
      body: `Change status of \"${tag.name}\"?`,
      okay: "Change",
      method: changeStatus,
      data: { id: tag.id, status: tag.status === 1 ? 0 : 1 },
    });
    setShowOptionsModal(true);
  };

  const changeStatus = async (data) => {
    const res = await changeTagStatus(data);
    if (res?.data?.success) {
      fetchTags(pagination);
    } else {
      handleErrors(res, navigate, "Tag Status:");
    }
    setShowOptionsModal(false);
  };

  const handleDeleteTag = (tag) => {
    setModalOptions({
      header: "Delete Tag",
      body: `Delete tag \"${tag.name}\"?`,
      okay: "Delete",
      method: deleteTag,
      data: { id: tag.id },
    });
    setShowOptionsModal(true);
  };

  const deleteTag = async (data) => {
    const res = await deleteTagById(data.id);
    if (res?.data?.success) {
      fetchTags(pagination);
    } else {
      handleErrors(res, navigate, "Delete Tag:");
    }
    setShowOptionsModal(false);
  };

  const handleCloseOptionsModal = () => setShowOptionsModal(false);
  const handleOpenAddEditModal = (tag) => {
    setSelectedTag(tag);
    setShowModal(true);
  };

  // Initialize DataTable
  useEffect(() => {
    const rows = tags.map((tag) => {
      const id = `<div>${tag.id}</div>`;
      const name = `<div>${tag.name}</div>`;
      const statusBtn = `<button class=\"btn btn-sm status-btn ${
        tag.status === 1 ? "text-success" : "text-danger"
      }\" data-id=\"${tag.id}\" title="change status">${tag.status === 1 ? "Active" : "Inactive"}</button>`;
      // only show delete if permission and not associated
      const canEdit = granted.some((p) => p.type === "update");
      const canDelete =
        granted.some((p) => p.type === "delete") && !tag.is_associated;
      const actions =
        (canEdit
          ? `<button class=\"btn btn-sm btn-outline-primary edit-btn me-1\" data-id=\"${tag.id}\" title="view ditails"><i class=\"fas fa-edit\"></i></button>`
          : "") +
        (canDelete && tag?.status === 0
          ? `<button class=\"btn btn-sm btn-outline-danger delete-btn\" data-id=\"${tag.id}\" title="delete"><i class=\"fas fa-trash-alt\"></i></button>`
          : "");
      return [id, name, statusBtn, actions];
    });

    const config = {
      data: rows,
      columns: [
        { title: "ID", className: "text-start" },
        { title: "Tag Name" },
        { title: "Status", className: "text-end", orderable: false },
        { title: "Actions", className: "text-end", orderable: false },
      ],
      destroy: true,
      responsive: true,
      searching: false,
      paging: false,
      pageLength: pagination.pageSize || 10,
    };

    if ($.fn.dataTable.isDataTable(tableRef.current)) {
      $(tableRef.current).DataTable().clear().rows.add(rows).draw();
    } else {
      $(tableRef.current).DataTable(config);
    }

    const $tbl = $(tableRef.current);
    $tbl
      .off("click", ".edit-btn")
      .on("click", ".edit-btn", (e) => {
        const id = +$(e.currentTarget).data("id");
        handleOpenAddEditModal(tags.find((t) => t.id === id));
      })
      .off("click", ".delete-btn")
      .on("click", ".delete-btn", (e) => {
        const id = +$(e.currentTarget).data("id");
        handleDeleteTag(tags.find((t) => t.id === id));
      })
      .off("click", ".status-btn")
      .on("click", ".status-btn", (e) => {
        const id = +$(e.currentTarget).data("id");
        handleStatusChange(tags.find((t) => t.id === id));
      });

    return () => {
      if ($.fn.dataTable.isDataTable(tableRef.current)) {
        $(tableRef.current).DataTable().destroy();
      }
    };
  }, [tags, pagination.pageSize, granted, pagination.currentPage]);

  return (
    <div className="container py-4 text-light bg-dark p-3 rounded mb-4">
      <div className="row align-items-center mb-4">
        <div className="col-md-6">
          <Form.Control
            type="text"
            placeholder="Search Tags..."
            value={query}
            onChange={handleSearch}
            className="bg-dark text-light border-light input-placeholder-light"
            style={{ borderRadius: "5px", maxWidth: "300px" }}
          />
        </div>
        <div className="col-md-6 text-end">
          {granted.some((p) => p.type === "create") && (
            <Button
              variant="success"
              title="Add new tag"
              onClick={() => handleOpenAddEditModal({})}
            >
              <i className="bi bi-plus-circle"></i> Add Tag
            </Button>
          )}
        </div>
      </div>

      <div className="table-responsive">
        <table
          ref={tableRef}
          className="table table-striped table-dark w-100"
        />
      </div>
      <div className="d-flex justify-content-end mt-2">
        <div className="mx-4 py-2">
          <span className="text-light">{`Rows/Page ${pagination.pageSize || 0}, Total records ${pagination?.totalRecords || 0}`}</span>
        </div>
        <BSPagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          siblingCount={2}
          showFirstLast
        />
      </div>

      <AddEditTagModal
        selectedTag={selectedTag}
        setSelectedTag={setSelectedTag}
        showModal={showModal}
        setShowModal={setShowModal}
        fetchTags={fetchTags}
        pagination={pagination}
      />

      <Modal
        show={showOptionsModal}
        onHide={handleCloseOptionsModal}
        centered
        className="text-light"
      >
        <Modal.Header closeButton className="bg-dark border-0">
          <Modal.Title>{modalOptions.header}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-dark">{modalOptions.body}</Modal.Body>
        <Modal.Footer className="bg-dark border-0">
          <Button variant="secondary" onClick={handleCloseOptionsModal}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => modalOptions.method(modalOptions.data)}
          >
            {modalOptions.okay}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>
        {`

          `}
      </style>
    </div>
  );
};

export default TagList;
